// ===========================================================================
// useBillingDashboard — datos del dashboard de Billing & Revenue Integrity.
//
// Reusa ceoDataService (mismos JSON reales de cost + coding del Executive).
// Deriva KPIs orientados al equipo de billing/integridad de ingresos:
//   - Revenue at risk (RAF$ estimado de oportunidades abiertas)
//   - Open coding opportunities / recapture rate
//   - Cost of care + breakdown + tendencia mensual
//   - Claims procesados (claimCount de facilities)
// Lo que falta (claims/AR/denials) viene de constantes estándar.
// ===========================================================================
import { useState, useEffect, useMemo } from "react";
import * as ceoService from "../services/ceoDataService";
import { STANDARD_RAF_VALUE_PER_OPP } from "../constants/billingDashboard";

export default function useBillingDashboard() {
  const [cost, setCost] = useState(null); // { summary, monthly, byLocation, topFacilities }
  const [coding, setCoding] = useState(null); // { summary, topHCCs, recaptureByPCP }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const results = await Promise.allSettled([
        ceoService.getCostSummary(), // 0
        ceoService.getCostMonthly(), // 1
        ceoService.getCostByLocation(), // 2
        ceoService.getCostTopFacilities(), // 3
        ceoService.getCodingSummary(), // 4
        ceoService.getCodingTopHCCs(), // 5
        ceoService.getCodingRecaptureByPCP(), // 6
      ]);
      if (cancelled) return;
      const val = (i) => (results[i].status === "fulfilled" ? results[i].value : null);

      setCost({
        summary: val(0),
        monthly: val(1),
        byLocation: val(2),
        topFacilities: val(3),
      });
      setCoding({
        summary: val(4),
        topHCCs: val(5),
        recaptureByPCP: val(6),
      });

      const firstError = results.find((r) => r.status === "rejected");
      if (firstError) setError(firstError.reason);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── KPIs derivados ─────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const codingSummary = coding?.summary || {};
    const costSummary = cost?.summary || {};

    const openOpps = codingSummary.notAddressedCount ?? codingSummary.totalOpportunities ?? 0;
    const totalOpps = codingSummary.totalOpportunities ?? 0;
    const addressed = Math.max(0, totalOpps - openOpps);
    const recaptureRate = totalOpps > 0 ? Math.round((addressed / totalOpps) * 100) : 0;

    // Revenue at risk (VALOR ESTÁNDAR): oportunidades abiertas × valor estándar.
    const revenueAtRisk = openOpps * STANDARD_RAF_VALUE_PER_OPP;

    // Claims procesados (real): suma de claimCount por facility.
    const claimsProcessed = (cost?.topFacilities?.facilities || []).reduce(
      (acc, f) => acc + (f.claimCount || 0),
      0
    );

    return {
      revenueAtRisk,
      openOpps,
      totalOpps,
      recaptureRate,
      totalCostOfCare: costSummary.totalCostOfCare ?? 0,
      avgCostPerPatient: costSummary.avgCostPerPatient ?? 0,
      highCostPatients: costSummary.highCostPatients ?? 0,
      claimsProcessed,
      uniquePatients: codingSummary.uniquePatients ?? 0,
    };
  }, [cost, coding]);

  // Breakdown de condition type (Dropped/Suspect/Pharmacy) con $ estándar.
  const captureByType = useMemo(() => {
    const bd = coding?.summary?.conditionTypeBreakdown || {};
    return Object.entries(bd).map(([type, count]) => ({
      type,
      count,
      estValue: count * STANDARD_RAF_VALUE_PER_OPP,
    }));
  }, [coding]);

  // Top HCCs por valor estimado (real count × valor estándar).
  const topHccByValue = useMemo(() => {
    const list = coding?.topHCCs?.topHCCs || [];
    return list
      .map((h) => ({
        hccId: h.hccId,
        description: h.hccDescription,
        count: h.count,
        priority: h.priority,
        estValue: (h.count || 0) * STANDARD_RAF_VALUE_PER_OPP,
      }))
      .slice(0, 8);
  }, [coding]);

  return {
    loading,
    error,
    cost,
    coding,
    kpis,
    captureByType,
    topHccByValue,
  };
}
